import React, { createContext, FC, PureComponent, ReactNode, useEffect, useRef, useState } from "react";
import { countyKey, EditorCounty, EditorState, EditorZip, stateKey, zipKey } from "./types";
import { BasicState, GeographyService } from "@app/geography";
import RegionSetService from "../regionSetService";
import { CountyLite } from "@app/geography/county";
import { ZipLite } from "@app/geography/zip";
import { RegionSetStatusType } from "@app/projects/enumerators";
import { CheckboxStateEnum } from "@app/shared/qes-standards/forms/Checkbox";
import RegionSet, { RegionGranularity } from "../regionSet";
import { CountyStatus, ServiceAreaUploadReturnModel } from "@app/projects/create/interfaces";
import { toast } from "react-toastify";

interface ServiceAreaEditorContextIncomingProps {
  children: ReactNode;
  // Used to figure out county statuses for the initial render
  countyStatuses: CountyStatus[];
  // Used to help determine and restrict county status options
  isZipProject?: boolean;
  // Used to figure out which regions are selected for the initial render
  regionSet: RegionSet;
  // Only used for the Download Service Area button
  projectName: string;
}

export interface ServiceAreaEditorContextOutgoingProps {
  countyFilter: string;
  countyLookup: { [countySSA: string]: EditorCounty };
  countyToZipLookup: { [countySSA: string]: string[] };
  isZipProject: boolean;
  loading: boolean;
  renderedStates: EditorState[];
  selectedState: string;
  stateLookup: { [stateAbbreviation: string]: EditorState };
  stateToCountyLookup: { [stateAbbreviation: string]: string[] };
  zipLookup: { [zipCode: string]: EditorZip };

  setCountyFilter: React.Dispatch<React.SetStateAction<string>>;
  setCountyLookup: React.Dispatch<React.SetStateAction<{ [countySSA: string]: EditorCounty }>>;
  setCountyToZipLookup: React.Dispatch<React.SetStateAction<{ [countySSA: string]: string[] }>>;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setRenderedStates: React.Dispatch<React.SetStateAction<EditorState[]>>;
  setSelectedState: React.Dispatch<React.SetStateAction<string>>;
  setStateLookup: React.Dispatch<React.SetStateAction<{ [stateAbbreviation: string]: EditorState }>>;
  setStateToCountyLookup: React.Dispatch<React.SetStateAction<{ [stateAbbreviation: string]: string[] }>>;
  setZipLookup: React.Dispatch<React.SetStateAction<{ [zipCode: string]: EditorZip }>>;

  convertLookupsToRegionSet: () => RegionSet;
  fileSubmit: (data: FormData) => Promise<void>;
  handleComplete: (onComplete: (regionSet: RegionSet) => void) => void;
  handleDownloadServiceAreaClick: () => Promise<void>;
}

/* eslint-disable sort-keys */
const initialValues: ServiceAreaEditorContextOutgoingProps = {
  // Getters
  countyFilter: "",
  countyLookup: undefined,
  countyToZipLookup: undefined,
  isZipProject: false,
  loading: true,
  renderedStates: [],
  selectedState: "",
  stateLookup: undefined,
  stateToCountyLookup: undefined,
  zipLookup: undefined,

  // Setters
  setCountyFilter: () => null,
  setCountyLookup: () => null,
  setCountyToZipLookup: () => null,
  setLoading: () => null,
  setRenderedStates: () => null,
  setSelectedState: () => null,
  setStateLookup: () => null,
  setStateToCountyLookup: () => null,
  setZipLookup: () => null,

  // Other Methods
  convertLookupsToRegionSet: () => null,
  fileSubmit: () => null,
  handleComplete: () => null,
  handleDownloadServiceAreaClick: () => null,
}
/* eslint-enable sort-keys */

export const ServiceAreaEditorContext = createContext(initialValues);

const ServiceAreaContextProvider: FC<ServiceAreaEditorContextIncomingProps> = (props: ServiceAreaEditorContextIncomingProps) => {
  const geographyService = useRef(new GeographyService()).current;
  const regionSetService = useRef(new RegionSetService()).current;

  const [loading, setLoading] = useState(initialValues.loading);
  const [countyFilter, setCountyFilter] = useState(initialValues.countyFilter);
  const [renderedStates, setRenderedStates] = useState<EditorState[]>(initialValues.renderedStates);
  const [selectedState, setSelectedState] = useState<string>(initialValues.selectedState);
  const [stateLookup, setStateLookup] = useState<{ [stateAbbreviation: string]: EditorState }>(initialValues.stateLookup);
  const [countyLookup, setCountyLookup] = useState<{ [countySSA: string]: EditorCounty }>(initialValues.countyLookup);
  const [stateToCountyLookup, setStateToCountyLookup] = useState<{ [stateAbbreviation: string]: string[] }>(initialValues.stateToCountyLookup);
  const [zipLookup, setZipLookup] = useState<{ [zipCode: string]: EditorZip }>(initialValues.zipLookup);
  const [countyToZipLookup, setCountyToZipLookup] = useState<{ [countySSA: string]: string[] }>(initialValues.countyToZipLookup);

  // fetch all the region information from the api
  useEffect(() => {
    const geoPromises = [
      geographyService.getStatesForDroplist(),
      geographyService.getCountiesLite(),
      geographyService.getZipsLite(),
    ];
    Promise.all(geoPromises).then((responses) => {
      const { includedCounties, includedStates, includedZips } = getIncludedRegionsFromRegionSet(props.regionSet);

      const states = responses[0] as BasicState[];
      const newStateLookup: { [abbr: string]: EditorState } = {};
      states.forEach((state) => {
        newStateLookup[state[stateKey]] = {
          ...state,
          checkboxState: CheckboxStateEnum.Unchecked, // we can't know this yet
        };
      });

      const counties = responses[1] as CountyLite[];
      const newCountyLookup: { [countySSA: string]: EditorCounty } = {};
      const newStateToCountyLookup: { [abbr: string]: string[] } = {};
      counties.forEach((county) => {
        newCountyLookup[county[countyKey]] = {
          ...county,
          checkboxState: CheckboxStateEnum.Unchecked,
        };
        const updatedCounties = newStateToCountyLookup[county.state] || [];
        updatedCounties.push(county[countyKey]);
        newStateToCountyLookup[county.state] = updatedCounties;
      });

      const zips = responses[2] as ZipLite[];
      const newZipLookup: { [zipCode: string]: EditorZip } = {};
      const newCountyToZipLookup: { [countySSA: string]: string[] } = {};
      zips.forEach((zip) => {
        zip.countySSAs.forEach((zipCountySSA) => {
          const key = zipKey(zipCountySSA, zip.zipCode);
          /**
           * zip checkbox should start checked if 
           * 1. it's explicitly included in the region set, or
           * 2. it's not included in the region set, but its primary county is in the region set,
           *    and none of the other zips in that county are in the region set
           */
          // condition 1
          const isCheckboxChecked = includedZips.includes(key) ||
            // condition 2: the county is in includedCounties, but the same county is not listed in includedZips
            (includedCounties.some((includedSSA) => includedSSA === zipCountySSA) &&
              !includedZips.some((includedZip) => includedZip.split(":")[0] === zipCountySSA));

          newZipLookup[key] = {
            ...zip,
            checkboxState: isCheckboxChecked ? CheckboxStateEnum.Checked : CheckboxStateEnum.Unchecked,
          };
          const updatedZips = newCountyToZipLookup[zipCountySSA] || [];
          updatedZips.push(zip.zipCode);
          newCountyToZipLookup[zipCountySSA] = [...new Set(updatedZips)];
        });
      });

      Object.keys(newStateLookup).forEach((stateAbbr) => {
        // unlike counties below, if a state is checked, all of its children (counties) are included in the region set
        const allCountiesIncluded = newStateToCountyLookup[stateAbbr].every((countySSA) => {
          return includedCounties.includes(countySSA) &&
            (newCountyToZipLookup[countySSA] || []).every((zipCode) => newZipLookup[zipKey(countySSA, zipCode)].checkboxState === CheckboxStateEnum.Checked);
        });
        newStateLookup[stateAbbr] = {
          ...newStateLookup[stateAbbr],
          checkboxState: includedStates.includes(stateAbbr) ?
            allCountiesIncluded ?
              CheckboxStateEnum.Checked :
              CheckboxStateEnum.Indeterminate :
            CheckboxStateEnum.Unchecked,
        }
      });

      Object.keys(newCountyLookup).forEach((countySSA) => {
        // 1. not every county has zip codes/is in the county to zip lookup
        // 2. If a county was selected and is not partial, none of its zips are included in the region set.
        //    So it's fully checked if all OR none of them are included, as long as the county is included
        const isCountyChecked = (newCountyToZipLookup[countySSA] || []).every((zipCode) => includedZips.includes(zipKey(countySSA, zipCode))) ||
          (newCountyToZipLookup[countySSA] || []).every((zipCode) => !includedZips.includes(zipKey(countySSA, zipCode))) ||
          (newCountyToZipLookup[countySSA] || []).length === 0;

        // The checkbox state no longer has any connection to whether a county is included in the service area
        // But we still use it here to help determine the county status
        const newCheckboxState = includedCounties.includes(countySSA) ?
          isCountyChecked ?
            CheckboxStateEnum.Checked :
            CheckboxStateEnum.Indeterminate :
          CheckboxStateEnum.Unchecked;
        let status = props.countyStatuses.find((countyStatusObj) => countyStatusObj[countyKey] === countySSA)?.status || RegionSetStatusType.Unknown;
        if (props.isZipProject &&
          (
            status === RegionSetStatusType.Approved ||
            status === RegionSetStatusType.Opportunity ||
            status === RegionSetStatusType.Pending
          )) {
          status = RegionSetStatusType.Active;
        }
        newCountyLookup[countySSA] = {
          ...newCountyLookup[countySSA],
          checkboxState: CheckboxStateEnum.Unchecked,
          // Projects that haven't changed service area since before approximately summer 2019 
          // didn't have county statuses, so we treat included counties with statuses of 
          // Unknown as Active just to be safe. Unknown otherwise means "Unselected."
          countyStatus: newCheckboxState !== CheckboxStateEnum.Unchecked && status === RegionSetStatusType.Unknown ? RegionSetStatusType.Active : status,
        }
      });

      setRenderedStates(states);
      setStateLookup(newStateLookup);
      setCountyLookup(newCountyLookup);
      setStateToCountyLookup(newStateToCountyLookup);
      setZipLookup(newZipLookup);
      setCountyToZipLookup(newCountyToZipLookup);
      setLoading(false);
    });
  }, []);

  const getIncludedRegionsFromRegionSet = (regionSet: RegionSet) => {
    const includedStates: string[] = [];
    const includedCounties: string[] = [];
    const includedZips: string[] = [];
    if (regionSet) {
      if (regionSet.granularity === RegionGranularity.MultiRegion) {
        const children = regionSet.partialRegionSets;
        children.forEach((state) => {
          if (state.granularity === RegionGranularity.State) {
            includedStates.push(state.regionCode); // abbreviation
            state.partialRegionSets.forEach((county) => {
              if (county.granularity === RegionGranularity.County) {
                includedCounties.push(county.regionCode); // county ssa
                county.partialRegionSets.forEach((countyZip) => {
                  if (countyZip.granularity === RegionGranularity.CountyZip) {
                    includedZips.push(zipKey(countyZip.regionCode, countyZip.subRegionCode)); // county ssa:zip code
                  }
                });
              }
            });
          }
        });
      }
    }
    return { includedCounties, includedStates, includedZips };
  }

  const fileSubmit = async (data: FormData) => {
    setLoading(true);
    return new Promise<void>((resolve, reject) => {
      (async () => {
        let newServiceAreaUploadReturnModel: ServiceAreaUploadReturnModel;
        try {
          newServiceAreaUploadReturnModel = await regionSetService.createFromFile(data, props.isZipProject);
          resolve();
        } catch (error) {
          setLoading(false);
          reject(error.responseText);
        }

        // this is all largely copied from the initial page load,
        // but different enough that it's not easy to just make a reusable function
        const { includedCounties, includedStates, includedZips } = getIncludedRegionsFromRegionSet(newServiceAreaUploadReturnModel.regionSet);
        const states = Object.values(stateLookup);
        const updatedStateLookup: { [abbr: string]: EditorState } = structuredClone(stateLookup);
        states.forEach((state) => {
          updatedStateLookup[state[stateKey]] = {
            ...state,
            checkboxState: CheckboxStateEnum.Unchecked, // we can't know this yet
          };
        });

        const counties = Object.values(countyLookup);
        const updatedCountyLookup: { [countySSA: string]: EditorCounty } = structuredClone(countyLookup);
        counties.forEach((county) => {
          updatedCountyLookup[county[countyKey]] = {
            ...county,
            checkboxState: CheckboxStateEnum.Unchecked,
          };
        });

        const zips = Object.values(zipLookup);
        const updatedZipLookup: { [zipCode: string]: EditorZip } = {};
        zips.forEach((zip) => {
          zip.countySSAs.forEach((zipCountySSA) => {
            const key = zipKey(zipCountySSA, zip.zipCode);
            /**
             * zip checkbox should start checked if 
             * 1. it's explicitly included in the region set, or
             * 2. it's not included in the region set, but its primary county is in the region set,
             *    and none of the other zips in that county are in the region set
             */
            // condition 1
            const isCheckboxChecked = includedZips.includes(key) ||
              // condition 2: the county is in includedCounties, but the same county is not listed in includedZips
              (includedCounties.some((includedSSA) => includedSSA === zipCountySSA) &&
                !includedZips.some((includedZip) => includedZip.split(":")[0] === zipCountySSA));
            updatedZipLookup[key] = {
              ...zip,
              checkboxState: isCheckboxChecked ? CheckboxStateEnum.Checked : CheckboxStateEnum.Unchecked,
            };
          });
        });

        Object.keys(updatedStateLookup).forEach((stateAbbr) => {
          // unlike counties below, if a state is checked, all of its children (counties) are included in the region set
          const allCountiesIncluded = stateToCountyLookup[stateAbbr].every((countySSA) => includedCounties.includes(countySSA));
          updatedStateLookup[stateAbbr] = {
            ...updatedStateLookup[stateAbbr],
            checkboxState: includedStates.includes(stateAbbr) ?
              allCountiesIncluded ?
                CheckboxStateEnum.Checked :
                CheckboxStateEnum.Indeterminate :
              CheckboxStateEnum.Unchecked,
          }
        });

        Object.keys(updatedCountyLookup).forEach((countySSA) => {
          // 1. not every county has zip codes/is in the county to zip lookup
          // 2. If a county was selected and is not partial, none of its zips are included in the region set.
          //    So it's fully checked if all OR none of them are included, as long as the county is included
          const isCountyChecked = (countyToZipLookup[countySSA] || []).every((zipCode) => includedZips.includes(zipKey(countySSA, zipCode))) ||
            (countyToZipLookup[countySSA] || []).every((zipCode) => !includedZips.includes(zipKey(countySSA, zipCode))) ||
            (countyToZipLookup[countySSA] || []).length === 0;
          const checkboxStatus = includedCounties.includes(countySSA) ?
            isCountyChecked ?
              CheckboxStateEnum.Checked :
              CheckboxStateEnum.Indeterminate :
            CheckboxStateEnum.Unchecked;
          let status = checkboxStatus === CheckboxStateEnum.Unchecked ? RegionSetStatusType.Unknown : newServiceAreaUploadReturnModel.countyStatuses?.find((countyStatusObj) => countyStatusObj[countyKey] === countySSA)?.status || RegionSetStatusType.Active;
          if (props.isZipProject &&
            (
              status === RegionSetStatusType.Approved ||
              status === RegionSetStatusType.Opportunity ||
              status === RegionSetStatusType.Pending
            )) {
            status = RegionSetStatusType.Active;
          }
          updatedCountyLookup[countySSA] = {
            ...updatedCountyLookup[countySSA],
            countyStatus: status,
          }
        });

        setLoading(false);
        setStateLookup(updatedStateLookup);
        setCountyLookup(updatedCountyLookup);
        setZipLookup(updatedZipLookup);
      })();
    });
  };

  const handleComplete = (onComplete: (regionSet: RegionSet) => void) => {
    const regionSet = convertLookupsToRegionSet();
    regionSetService.create(regionSet).then((realizedRegionSet) => {
      onComplete(realizedRegionSet);
    });
  }

  const handleDownloadServiceAreaClick = async () => {
    const regionSet = convertLookupsToRegionSet();
    try {
      const realizedRegionSet = await regionSetService.create(regionSet);
      const countyStatuses = Object.keys(countyLookup)
        .reduce<{ [countySSA: string]: RegionSetStatusType }>((result, countySSA) => {
          if (countyLookup[countySSA].countyStatus !== RegionSetStatusType.Unknown) {
            result[countySSA] = countyLookup[countySSA].countyStatus;
          }
          return result;
        }, {});

      const file = await geographyService.downloadServiceArea(props.projectName, realizedRegionSet.regionSetId, countyStatuses);
      // Despite the File typing on both ends, the endpoint is returning the contents of the file as a string
      // so we'll make it a file here and simulate the click to download
      // the reports section of QES does it similarly, just with more authentication and a real link
      const downloadLink = document.createElement("a");
      downloadLink.id = "service-area-download-link";
      downloadLink.setAttribute("download", `${props.projectName}-service-area.csv`);
      downloadLink.setAttribute("href", `data:text/plain;charset=utf-8,${encodeURIComponent(file as unknown as string)}`);
      document.body.appendChild(downloadLink);
      const clickEvent = new MouseEvent("click");
      downloadLink.dispatchEvent(clickEvent);
      document.body.removeChild(downloadLink);
      toast("Your service area has downloaded successfully.", {
        containerId: "z-index-1030",
        type: "success",
      });
    } catch {
      toast("We encountered an issue downloading your service area.", {
        containerId: "z-index-1030",
        type: "error",
      });
    }
  }

  const convertLookupsToRegionSet = (): RegionSet => {
    if (!stateLookup || !countyLookup || !zipLookup) {
      return null;
    }

    const includedStates = Object.values(stateLookup).filter((state) => state.checkboxState !== CheckboxStateEnum.Unchecked);
    const regionSet: RegionSet[] = includedStates.map((state) => {
      // get partial region sets (counties)
      const includedCounties = stateToCountyLookup[state[stateKey]]
        .map((countySSA) => countyLookup[countySSA])
        .filter((county) => countyLookup[county[countyKey]].countyStatus !== RegionSetStatusType.Unknown);
      const countyPartials = includedCounties.map((county) => {
        // get partial region sets (countyZips)
        const includedZips = (countyToZipLookup[county[countyKey]] || [])
          .map((zipCode) => zipLookup[zipKey(county[countyKey], zipCode)])
          .filter((zip) => zipLookup[zipKey(county[countyKey], zip.zipCode)].checkboxState === CheckboxStateEnum.Checked);

        // if every zip is selected, the region set doesn't care because it's the same as no zips
        const zipPartials: RegionSet[] = includedZips.length === (countyToZipLookup[county[countyKey]] || []).length ?
          [] :
          includedZips.map<RegionSet>((zip) => {
            return {
              granularity: RegionGranularity.CountyZip,
              partialRegionSets: null,
              regionCode: county[countyKey],
              regionSetId: null,
              subRegionCode: zip.zipCode,
            };
          });
        return {
          granularity: RegionGranularity.County,
          partialRegionSets: zipPartials,
          regionCode: county[countyKey],
          regionSetId: null as number,
          subRegionCode: null as string,
        };
      })

      return {
        granularity: RegionGranularity.State,
        partialRegionSets: countyPartials,
        regionCode: state[stateKey],
        regionSetId: null as number,
        subRegionCode: null as string,
      }
    });

    const multiRegionSet: RegionSet = {
      granularity: RegionGranularity.MultiRegion,
      partialRegionSets: regionSet,
      regionCode: null,
      regionSetId: null,
      subRegionCode: null,
    };
    return multiRegionSet;
  }


  /* eslint-disable sort-keys */
  return (
    <ServiceAreaEditorContext.Provider
      value={{
        // Getters
        countyFilter,
        countyLookup,
        countyToZipLookup,
        isZipProject: props.isZipProject,
        loading,
        renderedStates,
        selectedState,
        stateLookup,
        stateToCountyLookup,
        zipLookup,

        // Setters
        setCountyFilter,
        setCountyLookup,
        setCountyToZipLookup,
        setLoading,
        setRenderedStates,
        setSelectedState,
        setStateLookup,
        setStateToCountyLookup,
        setZipLookup,

        // Other Methods
        convertLookupsToRegionSet,
        fileSubmit,
        handleComplete,
        handleDownloadServiceAreaClick,
      }}>
      {props.children}
    </ServiceAreaEditorContext.Provider>
  );
  /* eslint-enable sort-keys */
}

export function withServiceAreaEditorContext<T>(Component: React.ComponentType<React.PropsWithChildren<T & ServiceAreaEditorContextOutgoingProps>>): React.ComponentType<React.PropsWithChildren<T>> {
  class WithServiceAreaEditorContext extends PureComponent<T> {
    public render() {
      return (
        <ServiceAreaEditorContext.Consumer>
          {(context) => <Component {...this.props} {...context as ServiceAreaEditorContextOutgoingProps} />}
        </ServiceAreaEditorContext.Consumer>
      );
    }
  }

  return WithServiceAreaEditorContext;
}

export default ServiceAreaContextProvider;